import React, { useState, useMemo } from 'react';
import { User, Order, Transaction, OrderStatus } from '../types';
import { Button, Card, Input, StatusBadge } from './ui';
import { ArrowLeft, DollarSign, FileText, Download, Activity, CheckCircle, TrendingUp, Search } from 'lucide-react';
import { generatePDF } from '../utils/pdfGenerator';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

interface DoctorDetailViewProps {
    doctor: User;
    orders: Order[];
    transactions: Transaction[];
    onClose: () => void;
}

export const DoctorDetailView: React.FC<DoctorDetailViewProps> = ({
    doctor,
    orders,
    transactions,
    onClose
}) => {
    const [activeTab, setActiveTab] = useState<'overview' | 'cases' | 'financials' | 'invoice'>('overview');

    // -- Invoice State --
    const [invoiceDateRange, setInvoiceDateRange] = useState({
        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [invoiceItems, setInvoiceItems] = useState<any[]>([]);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    // -- Derived Data --
    // Filter orders for this specific doctor (using doctorName matching for now based on typical schema, or userId if linked)
    // NOTE: Based on AdminPanel and previous context, orders have 'doctorName' property. 
    // Ideally, we should match by ID if available, but let's stick to what we see.
    // However, looking at types.ts, there wasn't a strict `doctorId` on Order, but `AdminPanel` logic might imply it.
    // Let's rely on filtering by name or if there's a doctorId field on Order (User.id).
    // Let's assume matches by name or if we add doctorId to order.
    // Looking at AdminPanel line 263: row.order.doctorName.
    // Safest bet for now: filter by doctorName matching user.name 
    const doctorOrders = useMemo(() => orders.filter(o => o.doctorName === doctor.name), [orders, doctor.name]);

    const completedOrders = useMemo(() => doctorOrders.filter(o => o.status === OrderStatus.DELIVERED), [doctorOrders]);

    const totalCases = doctorOrders.length;
    const activeCases = doctorOrders.filter(o => o.status !== OrderStatus.DELIVERED && o.status !== OrderStatus.CANCELLED).length;
    const totalSpent = completedOrders.reduce((acc, o) => acc + o.price, 0);

    // -- Helpers --
    const handleGenerateInvoicePreview = () => {
        const start = new Date(invoiceDateRange.start);
        const end = new Date(invoiceDateRange.end);
        end.setHours(23, 59, 59, 999);

        const items = completedOrders.filter(o => {
            const d = new Date(o.createdAt);
            return d >= start && d <= end;
        }).map(o => ({
            orderId: o.id,
            patient: o.patientName,
            date: o.createdAt,
            itemsCount: o.items.length,
            details: o.items.map(i => ({
                service: i.serviceName,
                teeth: i.teeth.join(', ')
            })),
            total: o.price
        }));
        setInvoiceItems(items);
    };

    const handleDownloadInvoice = async () => {
        setIsGeneratingPdf(true);
        setTimeout(async () => {
            try {
                const element = document.getElementById('invoice-preview');
                if (!element) throw new Error('Element not found');
                await generatePDF('invoice-preview', `Invoice_Dr_${doctor.name}_${invoiceDateRange.start}`);
            } catch (error) {
                console.error(error);
                alert('Failed to generate PDF');
            } finally {
                setIsGeneratingPdf(false);
            }
        }, 500);
    };

    // -- Renderers --

    const renderOverview = () => (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fade-in">
            <Card className="p-6 bg-zinc-900 border-zinc-800">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500"><Activity size={24} /></div>
                    <div>
                        <h3 className="text-2xl font-bold text-white">{activeCases}</h3>
                        <p className="text-zinc-500 text-sm">Active Cases</p>
                    </div>
                </div>
            </Card>
            <Card className="p-6 bg-zinc-900 border-zinc-800">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center text-green-500"><CheckCircle size={24} /></div>
                    <div>
                        <h3 className="text-2xl font-bold text-white">{totalCases}</h3>
                        <p className="text-zinc-500 text-sm">Total Cases</p>
                    </div>
                </div>
            </Card>
            <Card className="p-6 bg-zinc-900 border-zinc-800">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center text-yellow-500"><DollarSign size={24} /></div>
                    <div>
                        <h3 className="text-2xl font-bold text-white">{totalSpent.toLocaleString()} EGP</h3>
                        <p className="text-zinc-500 text-sm">Total Spent</p>
                    </div>
                </div>
            </Card>

            {/* Recent Orders */}
            <div className="md:col-span-3">
                <h3 className="text-lg font-bold text-white mb-4">Recent Cases</h3>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                            <tr>
                                <th className="p-4">Date</th>
                                <th className="p-4">Patient</th>
                                <th className="p-4">Status</th>
                                <th className="p-4 text-right">Cost</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                            {doctorOrders.slice(0, 5).map(o => (
                                <tr key={o.id} className="hover:bg-zinc-800/50">
                                    <td className="p-4 text-zinc-400">{new Date(o.createdAt).toLocaleDateString()}</td>
                                    <td className="p-4 text-white">{o.patientName}</td>
                                    <td className="p-4"><StatusBadge status={o.status} /></td>
                                    <td className="p-4 text-right font-mono text-white">{o.price}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderFinancials = () => {
        // Filter transactions for this doctor profile
        const doctorTransactions = transactions
            .filter(t => t.profileId === doctor.id)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        return (
            <div className="space-y-6 animate-fade-in">
                <div className="grid grid-cols-2 gap-6">
                    <Card className="p-6 bg-zinc-900 border-zinc-800">
                        <div className="text-sm text-zinc-500 uppercase mb-1">Current Balance</div>
                        <div className={`text-3xl font-bold ${doctor.balance < 0 ? 'text-red-400' : 'text-green-400'}`}>
                            {doctor.balance.toLocaleString()} EGP
                        </div>
                        <p className="text-xs text-zinc-600 mt-2">{doctor.balance < 0 ? 'Outstanding Due' : 'Credit Available'}</p>
                    </Card>
                    <Card className="p-6 bg-zinc-900 border-zinc-800">
                        <div className="text-sm text-zinc-500 uppercase mb-1">Total Lifetime Spent</div>
                        <div className="text-3xl font-bold text-white">
                            {totalSpent.toLocaleString()} EGP
                        </div>
                    </Card>
                </div>

                <h3 className="text-xl font-bold text-white">Transaction History</h3>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                            <tr>
                                <th className="p-4">Date</th>
                                <th className="p-4">Type</th>
                                <th className="p-4">Description</th>
                                <th className="p-4 text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-800">
                            {doctorTransactions.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="p-8 text-center text-zinc-500">No transactions found.</td>
                                </tr>
                            ) : (
                                doctorTransactions.map(t => (
                                    <tr key={t.id} className="hover:bg-zinc-800/50">
                                        <td className="p-4 text-zinc-400">{new Date(t.createdAt).toLocaleDateString()}</td>
                                        <td className="p-4 text-zinc-300">
                                            <span className="bg-white/5 border border-white/10 px-2 py-1 rounded text-xs">
                                                {t.type.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="p-4 text-white">{t.description}</td>
                                        <td className={`p-4 text-right font-mono font-bold ${t.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {t.amount > 0 ? '+' : ''}{t.amount}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderInvoice = () => (
        <div className="space-y-6 animate-fade-in h-full flex flex-col">
            <div className="flex flex-col md:flex-row gap-4 items-end bg-zinc-900 border border-zinc-800 p-4 rounded-xl">
                <Input
                    label="Start Date"
                    type="date"
                    value={invoiceDateRange.start}
                    onChange={(e: any) => setInvoiceDateRange(prev => ({ ...prev, start: e.target.value }))}
                />
                <Input
                    label="End Date"
                    type="date"
                    value={invoiceDateRange.end}
                    onChange={(e: any) => setInvoiceDateRange(prev => ({ ...prev, end: e.target.value }))}
                />
                <Button onClick={handleGenerateInvoicePreview}>
                    <FileText size={18} /> Generate Preview
                </Button>
            </div>

            {invoiceItems.length > 0 && (
                <div className="flex-1 flex flex-col gap-4">
                    <div className="flex justify-between items-center">
                        <h3 className="text-white font-bold">Invoice Preview</h3>
                        <Button onClick={handleDownloadInvoice} disabled={isGeneratingPdf}>
                            {isGeneratingPdf ? 'Generating...' : <><Download size={18} /> Download PDF</>}
                        </Button>
                    </div>

                    <div className="flex-1 overflow-y-auto bg-black rounded-xl border border-zinc-800 p-8 shadow-2xl relative">
                        <div id="invoice-preview" className="max-w-3xl mx-auto bg-black text-white p-8 min-h-[1123px] flex flex-col justify-between">
                            <div>
                                {/* Invoice Header */}
                                <div className="flex justify-between items-start border-b border-zinc-800 pb-8 mb-8">
                                    <div className="flex items-center gap-4">
                                        <img
                                            src="https://pqcchblhugugkywsfofz.supabase.co/storage/v1/object/public/meida/logo.png"
                                            alt="Galal Dental Lab"
                                            className="h-20 w-auto object-contain"
                                            crossOrigin="anonymous"
                                        />
                                        <div>
                                            <h1 className="text-3xl font-bold tracking-tight text-white mb-2">INVOICE</h1>
                                            <div className="text-zinc-500 text-sm">
                                                <p>Date: {new Date().toLocaleDateString()}</p>
                                                <p>Period: {invoiceDateRange.start} to {invoiceDateRange.end}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <h2 className="text-xl font-bold text-blue-500">Galal Dental Lab</h2>
                                        <p className="text-zinc-400 text-sm mt-1">Client: Dr. {doctor.name}</p>
                                        <p className="text-zinc-500 text-xs">{doctor.email}</p>
                                        <p className="text-zinc-500 text-xs">{doctor.clinicName}</p>
                                    </div>
                                </div>

                                {/* Summary Cards */}
                                <div className="grid grid-cols-2 gap-4 mb-8">
                                    <div className="bg-zinc-900/50 p-4 rounded border border-zinc-800">
                                        <div className="text-zinc-500 text-xs uppercase mb-1">Total Cases</div>
                                        <div className="text-xl font-bold">{invoiceItems.length}</div>
                                    </div>
                                    <div className="bg-zinc-900/50 p-4 rounded border border-zinc-800 hover:border-green-500/30 transition-colors">
                                        <div className="text-zinc-500 text-xs uppercase mb-1">Total Amount</div>
                                        <div className="text-xl font-bold text-green-400">{invoiceItems.reduce((acc, i) => acc + i.total, 0).toLocaleString()} EGP</div>
                                    </div>
                                </div>

                                {/* Table */}
                                <table className="w-full text-left text-sm mb-8">
                                    <thead className="border-b border-zinc-800 text-zinc-500 text-xs uppercase">
                                        <tr>
                                            <th className="py-3">Date</th>
                                            <th className="py-3">Order ID</th>
                                            <th className="py-3">Patient</th>
                                            <th className="py-3">Details</th>
                                            <th className="py-3 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-zinc-900">
                                        {invoiceItems.map((item, idx) => (
                                            <tr key={idx}>
                                                <td className="py-3 text-zinc-400 align-top">{new Date(item.date).toLocaleDateString()}</td>
                                                <td className="py-3 font-mono text-zinc-500 align-top">#{item.orderId.slice(0, 6)}</td>
                                                <td className="py-3 align-top">{item.patient}</td>
                                                <td className="py-3 text-zinc-400 text-xs align-top">
                                                    <div className="space-y-1">
                                                        {item.details.map((d: any, i: number) => (
                                                            <div key={i} className="flex flex-col">
                                                                <span className="text-white">{d.service}</span>
                                                                {d.teeth && <span className="text-zinc-600">Tooth: {d.teeth}</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                                <td className="py-3 text-right font-mono align-top">{item.total}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="border-t border-zinc-700">
                                        <tr>
                                            <td colSpan={4} className="py-4 text-right font-bold text-white uppercase pr-8">Total</td>
                                            <td className="py-4 text-right font-bold font-mono text-green-400 text-lg">
                                                {invoiceItems.reduce((acc, i) => acc + i.total, 0).toLocaleString()} EGP
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>

                            {/* Footer */}
                            <div className="border-t border-zinc-800 pt-8 mt-auto text-center">
                                <h3 className="text-white font-bold mb-2">Thank you for trusting Galal Dental Lab</h3>
                                <p className="text-zinc-600 text-xs">Generated by Galal Dental Lab Management System</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="h-full flex flex-col bg-zinc-950 animate-fade-in-up">
            {/* Header */}
            <div className="flex items-center gap-4 border-b border-zinc-800 p-6 bg-zinc-900/50 sticky top-0 z-20 backdrop-blur-md">
                <Button variant="secondary" size="sm" onClick={onClose}>
                    <ArrowLeft size={16} /> Back
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                        Dr. {doctor.name}
                        <span className="text-xs bg-blue-900/30 text-blue-400 px-2 py-0.5 rounded border border-blue-500/20">Dentist</span>
                    </h1>
                    <p className="text-zinc-400 text-sm">{doctor.clinicName} • {doctor.email}</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-4 border-b border-zinc-800 overflow-x-auto">
                {['overview', 'cases', 'financials', 'invoice'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab as any)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab
                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                            : 'text-zinc-400 hover:bg-zinc-900 hover:text-white'
                            }`}
                    >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'invoice' && renderInvoice()}
                {activeTab === 'financials' && renderFinancials()}
                {activeTab === 'cases' && (
                    <div className="space-y-4">
                        <h2 className="text-xl font-bold text-white">Full Case History</h2>
                        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-zinc-950 text-zinc-500 text-xs font-bold uppercase">
                                    <tr>
                                        <th className="p-4">Date</th>
                                        <th className="p-4">Patient</th>
                                        <th className="p-4">Doctor Ref</th>
                                        <th className="p-4">Status</th>
                                        <th className="p-4 text-right">Items</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-zinc-800">
                                    {doctorOrders.map(o => (
                                        <tr key={o.id} className="hover:bg-zinc-800/50">
                                            <td className="p-4 text-zinc-400">{new Date(o.createdAt).toLocaleDateString()}</td>
                                            <td className="p-4 text-white font-bold">{o.patientName}</td>
                                            <td className="p-4 text-zinc-500">{o.doctorName}</td>
                                            <td className="p-4"><StatusBadge status={o.status} /></td>
                                            <td className="p-4 text-right text-zinc-300">{o.items.length}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
